import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { DataTransferService } from './data-transfer.service';
import { DataTransferOperation } from './data-transfer.interfaces';
import { DataTransferProgressDialogComponent } from './data-transfer-progress-dialog/data-transfer-progress-dialog.component';
import { UtilsService } from '../../../shared/utils.service';

@Component({
    selector: 'app-accounts-data-transfer',
    templateUrl: './accounts-data-transfer.component.html',
    styleUrls: ['./accounts-data-transfer.component.scss']
})
export class AccountsDataTransferComponent implements OnInit {

    @Input() accountId: number;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('importFileInput') importFileInput: ElementRef<HTMLInputElement>;

    history: DataTransferOperation[] = [];
    totalHistory: number = 0;
    pageSize: number = 10;
    isLoading: boolean = false;

    displayedColumns: string[] = [
        'type',
        'status',
        'started_at',
        'completed_at',
        'records',
        'initiated_by',
        'actions'
    ];

    constructor(
        private dataTransferService: DataTransferService,
        public utils: UtilsService
    ) {}

    ngOnInit(): void {
        this.loadHistory();
    }

    loadHistory(offset: number = 0): void {
        this.isLoading = true;
        this.dataTransferService.getHistory(this.accountId, {
            limit: this.pageSize,
            offset: offset
        }).then((response: any) => {
            this.history = response.data || [];
            this.totalHistory = response.total || 0;
        }).catch(() => {
            this.utils.showToast('Failed to load data transfer history.');
        }).finally(() => {
            this.isLoading = false;
        });
    }

    onPageChange(event: any): void {
        this.pageSize = event.pageSize;
        this.loadHistory(event.pageIndex);
    }

    onExport(): void {
        this.dataTransferService.getStatus(this.accountId)
            .then((response: any) => {
                const operation = response?.data;
                if (operation && (operation.status === 'queued' || operation.status === 'processing')) {
                    this.openProgressDialog(operation.type);
                    return;
                }

                this.utils.showModal(
                    'Confirm Export',
                    'Are you sure you want to export all data for this account? This may take several minutes.<br><br>' +
                    '<strong style="color: #c62828;">Confidential: Internal Use Only.</strong> Never share the exported file with clients, customers, or any external or third-party parties. Exports reveal the full database structure and are strictly for internal engineering and support use.',
                    () => {
                    this.dataTransferService.triggerExport(this.accountId).then(() => {
                        this.openProgressDialog('export');
                    }).catch((error: any) => {
                        if (error?.status === 409) {
                            this.utils.showToast('An operation is already in progress for this account.');
                            this.openProgressDialog('export');
                        } else {
                            this.utils.showToast('Failed to start export.');
                        }
                    });
                });
            })
            .catch(() => {
                this.utils.showToast('Could not check the current operation status. Please try again.');
            });
    }

    onImport(): void {
        this.dataTransferService.getStatus(this.accountId)
            .then((response: any) => {
                const operation = response?.data;
                if (operation && (operation.status === 'queued' || operation.status === 'processing')) {
                    this.openProgressDialog(operation.type);
                    return;
                }

                // Trigger the hidden file input via the template ref (no global DOM id).
                const fileInput: HTMLInputElement | undefined = this.importFileInput?.nativeElement;
                if (fileInput) {
                    fileInput.value = '';
                    fileInput.click();
                }
            })
            .catch(() => {
                this.utils.showToast('Could not check the current operation status. Please try again.');
            });
    }

    onFileSelected(event: any): void {
        const file: File = event.target.files[0];
        if (!file) {
            return;
        }

        if (!file.name.toLowerCase().endsWith('.zip')) {
            this.utils.showToast('Please select a ZIP file.');
            return;
        }

        this.dataTransferService.triggerImport(this.accountId, file).then(() => {
            this.openProgressDialog('import');
        }).catch((error: any) => {
            if (error?.status === 409) {
                this.utils.showToast('An operation is already in progress for this account.');
                this.openProgressDialog('import');
            } else {
                const message = error?.error?.message || 'Failed to start import.';
                this.utils.showToast(message);
            }
        });
    }

    openProgressDialog(type: string): void {
        this.utils.showComponentDialog(
            DataTransferProgressDialogComponent,
            {
                accountId: this.accountId,
                operationType: type
            },
            {
                width: '750px',
                disableClose: false
            },
            () => {
                this.loadHistory();
            }
        );
    }

    onDownload(operation: DataTransferOperation): void {
        this.dataTransferService.getDownloadInfo(this.accountId, operation.id)
            .then((response: any) => {
                if (!response?.data?.url) {
                    this.utils.showToast('The export file is no longer available.');
                    return;
                }
                this.utils.onDownloadFromTemporaryURL(response.data.url, response.data.custom_file_name);
            })
            .catch(() => {
                this.utils.showToast('Failed to download the export file.');
            });
    }

    onViewError(operation: DataTransferOperation): void {
        this.utils.showModal('Error Details', operation.error_message || 'No error details available.');
    }

    getStatusColor(status: string): string {
        switch (status) {
            case 'queued': return 'default';
            case 'processing': return 'primary';
            case 'completed': return 'accent';
            case 'failed': return 'warn';
            default: return 'default';
        }
    }

    getRecordCount(operation: DataTransferOperation): string {
        if (!operation.result_data) {
            return '-';
        }
        if (operation.type === 'export' && operation.result_data.record_counts) {
            const total = Object.values(operation.result_data.record_counts as Record<string, number>)
                .reduce((sum: number, count: number) => sum + count, 0);
            return total.toString();
        }
        if (operation.type === 'import' && operation.result_data.total_records !== undefined) {
            return operation.result_data.total_records.toString();
        }
        return '-';
    }
}
